<template>
  <div class="command-palette">
    <div class="command-palette-overlay" :class="{ 'is-active': isOpen }" @click="close"></div>
    <div class="command-palette-modal" :class="{ 'is-active': isOpen }" ref="modal" @keydown="handleModalKeyDown">
      <div class="command-palette-header">
        <div class="header-navigation">
          <span class="search-icon">
            <i class="mdi mdi-magnify"></i>
          </span>
        </div>
        
        <input 
          ref="searchInput"
          v-model="searchQuery"
          class="command-palette-search" 
          placeholder="Search menu items..."
        />
        <kbd v-if="isOpen" class="command-palette-kbd">ESC</kbd>
      </div>

      <div class="command-palette-content">
        <command-palette-list 
          :loading="isLoading"
          :empty-message="'No menu items found'"
          :is-empty="filteredItems.length === 0"
          :item-count="filteredItems.length"
          item-label="item(s)"
          :show-footer="true"
        >
          <command-palette-list-item
            v-for="(item, itemIndex) in filteredItems" 
            :key="itemIndex"
            :title="item.title"
            :subtitle="item.breadcrumb"
            :icon="item.icon"
            :active="activeIndex === itemIndex"
            @select="selectItem(itemIndex)"
            @mouseenter="setActiveItem(itemIndex)"
          />
        </command-palette-list>
      </div>
      
      <div class="command-palette-footer">
        <div class="footer-hint">
          <span>Navigate</span>
          <div class="hint-keys">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
          </div>
        </div>
        <div class="footer-hint">
          <span>Select</span>
          <div class="hint-keys">
            <kbd>Enter</kbd>
          </div>
        </div>
        <div class="footer-hint">
          <span>Close</span>
          <div class="hint-keys">
            <kbd>Esc</kbd>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CommandPaletteList from './command_palette/CommandPaletteList.vue'
import CommandPaletteListItem from './command_palette/CommandPaletteListItem.vue'

export default {
  name: 'CommandPalette',
  components: {
    CommandPaletteList,
    CommandPaletteListItem
  },
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      activeIndex: 0,
      isLoading: false
    }
  },
  computed: {
    filteredItems() {
      if (!this.searchQuery) return this.items
      
      const query = this.searchQuery.toLowerCase()
      return this.items.filter(item => {
        const title = item.title.toLowerCase()
        const breadcrumb = item.breadcrumb?.toLowerCase() || ''
        const keywords = item.keywords || []
        
        return title.includes(query) || 
               breadcrumb.includes(query) ||
               keywords.some(keyword => keyword.toLowerCase().includes(query))
      })
    }
  },
  watch: {
    searchQuery() {
      this.activeIndex = 0
    },
    
    filteredItems() {
      if (this.activeIndex >= this.filteredItems.length) {
        this.activeIndex = Math.max(0, this.filteredItems.length - 1)
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        this.open()
        return
      }
    },
    
    handleModalKeyDown(event) {
      if (!this.isOpen) return
      
      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }
      
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.navigateDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.navigateUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        this.handleEnter()
      }
    },
    
    open() {
      this.isOpen = true
      this.searchQuery = ''
      this.activeIndex = 0
      this.isLoading = false
      
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.modal) {
            this.$refs.modal.focus()
          }
          
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus()
          }
        }, 50)
      })
    },
    
    close() {
      this.isOpen = false
      this.searchQuery = ''
    },
    
    navigateDown() {
      if (this.filteredItems.length === 0) return
      
      this.activeIndex = (this.activeIndex + 1) % this.filteredItems.length
    },
    
    navigateUp() {
      if (this.filteredItems.length === 0) return
      
      this.activeIndex = (this.activeIndex - 1 + this.filteredItems.length) % this.filteredItems.length
    },
    
    handleEnter() {
      if (this.filteredItems.length > 0) {
        this.selectActiveItem()
      }
    },
    
    selectActiveItem() {
      if (this.filteredItems.length === 0) return
      
      const item = this.filteredItems[this.activeIndex]
      if (!item) return
      
      this.selectItem(this.activeIndex)
    },
    
    setActiveItem(itemIndex) {
      this.activeIndex = itemIndex
    },
    
    selectItem(itemIndex) {
      const item = this.filteredItems[itemIndex]
      if (!item) return
      
      if (item.handler && typeof item.handler === 'function') {
        item.handler()
        this.close()
      } else {
        this.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.command-palette {
  &-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    
    &.is-active {
      opacity: 1;
      visibility: visible;
    }
  }
  
  &-modal {
    position: fixed;
    top: 100px;
    left: 50%;
    transform: translateX(-50%) translateY(-20px);
    width: 600px;
    max-width: 90%;
    background-color: var(--ld-bg-secondary);
    border-radius: 8px;
    box-shadow: 0 8px 30px var(--ld-shadow-color);
    z-index: 999999;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 200px);
    
    &.is-active {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  &-header {
    padding: 12px 16px;
    border-bottom: 1px solid var(--ld-border-default);
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .header-navigation {
      display: flex;
      align-items: center;

      .search-icon {
        color: var(--ld-text-secondary);
        margin-right: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
      }
    }
  }
  
  &-search {
    flex: 1;
    border: none;
    outline: none;
    font-size: 16px;
    background: transparent;
    color: var(--ld-text-primary);
  }
  
  &-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  
  &-footer {
    padding: 8px 16px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid var(--ld-border-default);
    flex-shrink: 0;

    .footer-hint {
      display: flex;
      align-items: center;
      margin-left: 16px;
      font-size: 12px;
      color: var(--ld-text-secondary);
      
      span {
        margin-right: 4px;
      }
      
      .hint-keys {
        display: flex;
        
        kbd {
          margin-left: 2px;
          padding: 1px 4px;
        }
      }
    }
  }
  
  &-kbd {
    background: var(--ld-bg-primary);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 12px;
    font-family: monospace;
    color: var(--ld-text-primary);
  }
}
</style>